import {
  FlightPurchaseModel,
  FlightPurchaseMutationInput,
} from 'api-hooks/order/ticket-order';
import { TicketSaleModel } from 'api-hooks/sale/ticket-sale';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, FormState, Input } from 'components/elements';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import useRevertVoidAction from 'hooks/use-revert-void-action';
import useVoidAction from 'hooks/use-void-action';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import TitleStatus from 'legacy-modules/common/title-status';
import OtherPriceComponent from 'legacy-modules/order/component/other-price-component';
import ParentOrderRelatedTable from 'legacy-modules/order/component/parent-children-purchase-table';
import SaleRelatedTable from 'legacy-modules/order/component/sale-related-table';
import SalePurchaseRefundTable from 'legacy-modules/refund/components/sale-purchase-table';
import {
  HalfContainer,
  SectionContainer as RawSectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { TicketOrderFormSchema, TicketOrderFormType } from './form-type';
import OrdersDetailCard from './order-detail-card';
import ScheduleSectionComponent from './schedule';
import FlightPurchaseSelectInput from './select-input';
import SummaryFlightPurchase from './summary';
import { totalFlightPurchaseCalculation } from './total-calculation';
import useGetDefaultValues from '../hooks/use-get-default-values';
import { FormContent, FormDivider } from '../style';

interface Props {
  onSubmit: (
    input: FlightPurchaseMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<FlightPurchaseModel | undefined>;
  renderError?: React.ReactNode;
  flightPurchase?: FlightPurchaseModel;
  flightSale?: TicketSaleModel;
  defaultValues?: TicketOrderFormType;
}

export default function FlightPurchaseForm(props: Props) {
  const { t } = useTranslation();
  const { flightPurchase, renderError } = props;
  const { asPath } = useRouter();

  const defaultValues = useGetDefaultValues({
    flightPurchase,
    flightSale: props.flightSale,
    defaultValues: props.defaultValues,
  });

  const resolver = useYupValidationResolver(TicketOrderFormSchema());

  const methods = useForm<TicketOrderFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values: TicketOrderFormType) => {
      const {
        tempTransactionAt,
        agentStatus,
        flightSale,
        flightPurchase,
        //remove unused data
        ...rest
      } = values;

      try {
        const total = totalFlightPurchaseCalculation({
          flightPurchases: values.flightPurchases,
          discount: values.discount,
          otherCost: values.otherCost,
        });
        const needApproval =
          values.transactionAt?.getTime() !== tempTransactionAt?.getTime();

        const result = {
          ...rest,
          total,
          needApproval,
        };

        await props.onSubmit(result, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  // const { isActive, isDraft, isRejected } = usePurchaseStatus(flightPurchase);

  // const isShowButton = flightPurchase?.status === PurchaseStatusEnum.pending;
  const permission = useGetCrudPermission('flight-purchases');
  const { onVoid, voidMutate, isActive, voidPermission } = useVoidAction(
    'flight-purchases',
    flightPurchase,
  );
  const { resolverButton } = useResolveApproval(
    'flight-purchases',
    flightPurchase,
  );
  const { requestApprovalButton } = useApprovalAction(
    'flight-purchases',
    flightPurchase,
  );
  const { revertDraftButton } = useRevertDraftAction(
    'flight-purchases',
    flightPurchase,
  );
  const { revertVoidButton } = useRevertVoidAction(
    'flight-purchases',
    flightPurchase,
  );
  const onRenderRightComponent = React.useMemo(() => {
    if (flightPurchase) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {revertDraftButton}
          {resolverButton}
          {requestApprovalButton}
          {revertVoidButton}
        </div>
      );
    }
    return undefined;
  }, [
    flightPurchase,
    requestApprovalButton,
    resolverButton,
    revertDraftButton,
    revertVoidButton,
  ]);

  const onRelatedOrderAfterChange = React.useCallback(
    (value: FlightPurchaseModel) => {
      methods.setValue('parentRefId', value.refId);
      methods.setValue('agentId', value.agent?.id || '');
      methods.setValue('invoiceId', value.sale?.id || '');
      methods.setValue('tourId', value.tour?.id || '');
      methods.setValue('type', value.type || 'sales');
      methods.setValue('paymentChannelId', value.paymentChannel?.id || '');
    },
    [methods],
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!flightPurchase}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={
        flightPurchase &&
        `${t('common:ticket_order')} - ${flightPurchase.number}`
      }
      keyName={asPath}
    >
      <FormContent>
        {renderError}
        <FormState>
          {({ editable }) => (
            <FormHeader
              data={flightPurchase}
              noSubmit
              onDelete={onVoid}
              noCancel
              noDelete={editable || !voidPermission}
              title={
                flightPurchase ? (
                  <TitleStatus
                    type="purchase"
                    title={`${t('common:ticket_order')} - ${
                      flightPurchase.number
                    }`}
                    status={flightPurchase.status}
                  />
                ) : (
                  t('common:add_extra', { extra: t('common:ticket_order') })
                )
              }
              isDeleteLoading={voidMutate.isLoading}
              deleteText={t('common:void')}
              noEdit={!isActive || !permission.update}
              onRenderRightComponent={onRenderRightComponent}
            />
          )}
        </FormState>
        <div>
          {flightPurchase &&
            !!(flightPurchase?.releasePurchases || []).length && (
              <SalePurchaseRefundTable data={flightPurchase.releasePurchases} />
            )}
          {flightPurchase && (
            <SaleRelatedTable
              data={flightPurchase.sale}
              navigation={{
                sale: 'TicketSalesView',
                purchase: 'FlightPurchasesView',
              }}
            />
          )}
          {flightPurchase &&
            (!!flightPurchase.parentPurchase ||
              !!(flightPurchase.childrenPurchases || []).length) && (
              <ParentOrderRelatedTable
                data={flightPurchase}
                navigation="FlightPurchasesView"
              />
            )}
          <RawSectionContainer>
            <HalfContainer>
              <FlightPurchaseSelectInput
                label={t('common:related_order')}
                placeholder={t('common:choose_order')}
                name="parentRefId"
                onAfterDetailChange={onRelatedOrderAfterChange}
              />
            </HalfContainer>
          </RawSectionContainer>
          <RawSectionContainer>
            <HalfContainer>
              <Input
                type="text"
                name="code"
                label={t('ticketOrders:group_code')}
                placeholder={t('ticketOrders:group_code')}
              />
            </HalfContainer>
            <HalfContainer>
              <Input
                type="text-area"
                name="description"
                label={t('common:description')}
                placeholder={t('common:description')}
              />
            </HalfContainer>
          </RawSectionContainer>
          <SeparatorVertical $margin={16} />
          <OrdersDetailCard />
          <ScheduleSectionComponent />
          <SeparatorVertical $margin={24}>
            <div style={{ height: 2 }} />
          </SeparatorVertical>
          <OtherPriceComponent
            type="flight-purchases"
            data={props.flightPurchase}
          />
          <SeparatorVertical $margin={32} />
          <FormDivider />
        </div>
      </FormContent>
      <SummaryFlightPurchase flightPurchase={flightPurchase} />
    </Form>
  );
}
